import React, { useEffect, useState } from 'react';
import { api } from '../../api/client';
import Switch from './Switch';
import { LANGUAGES, LANGUAGE_KEYS, DEFAULT_ENABLED_LANGUAGES, FALLBACK_LANGUAGE } from '../../i18n/languages';

import { ta, taf } from '../../i18n/adminT';
// Documenti di cui il visitatore deve poter leggere la versione nella propria lingua
const REQUIRED_DOCS = [
  { type: 'privacy', label: 'privacy' },
  { type: 'security', label: 'sicurezza' },
];

// Pannello (solo super-admin): quali lingue può scegliere il visitatore nel
// pannello utente e nella pagina contatti, e quale appare per prima.
export default function LanguageSettings() {
  const [enabled, setEnabled] = useState(DEFAULT_ENABLED_LANGUAGES);
  const [defaultLang, setDefaultLang] = useState(FALLBACK_LANGUAGE);
  const [docs, setDocs] = useState({}); // "tipo:lingua" -> documento caricato
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadConfig = api.getLanguageConfig()
      .then((res) => {
        setEnabled(res.data.enabled);
        setDefaultLang(res.data.default);
      })
      .catch((err) => setError(err.message));

    const loadDocs = api.getDocuments()
      .then((res) => {
        const map = {};
        res.data.forEach((d) => { map[`${d.doc_type}:${d.lang}`] = d; });
        setDocs(map);
      })
      .catch(() => {}); // l'indicazione dei documenti è solo informativa

    Promise.all([loadConfig, loadDocs]).finally(() => setLoading(false));
  }, []);

  const toggleLanguage = (key, on) => {
    const next = LANGUAGE_KEYS.filter((k) => (k === key ? on : enabled.includes(k)));
    if (next.length === 0) return; // almeno una lingua deve restare attiva
    setEnabled(next);
    // Se si spegne la lingua predefinita, la prima rimasta prende il suo posto
    if (!next.includes(defaultLang)) setDefaultLang(next[0]);
    setSaved(false);
  };

  const handleSave = async () => {
    setError('');
    setSaved(false);
    setSaving(true);
    try {
      const res = await api.saveLanguageConfig({ enabled, default: defaultLang });
      setEnabled(res.data.enabled);
      setDefaultLang(res.data.default);
      setSaved(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <div>
      <h1 className="admin-page-title">{ta('Lingue')}</h1>
      <p className="admin-page-subtitle">
        {ta('Scegli in quali lingue i visitatori possono usare il pannello utente e la pagina contatti, e quale compare per prima. Disattivare una lingua non cancella documenti né testi delle email: la nasconde soltanto, e chi l\'aveva scelta passa alla lingua predefinita.')}
      </p>

      <div className="admin-card" style={{ maxWidth: 680 }}>
        {error && <div className="form-error">{error}</div>}
        {saved && (
          <div style={{ background: 'rgba(62,142,126,0.12)', color: 'var(--teal-green-dark)', borderRadius: 'var(--radius-control)', padding: '10px 14px', fontSize: '0.85rem', marginBottom: 16 }}>
            {ta('Impostazioni salvate.')}
          </div>
        )}

        {LANGUAGES.map(({ key, label, flag }) => {
          const isOn = enabled.includes(key);
          const isOnlyOne = isOn && enabled.length === 1;
          const missing = REQUIRED_DOCS.filter((d) => !docs[`${d.type}:${key}`]);
          // Per l'italiano non c'è una versione di ripiego: se manca, manca e basta
          const warn = isOn && key !== FALLBACK_LANGUAGE && missing.length > 0;

          return (
            <div
              key={key}
              style={{ padding: '14px 0', borderBottom: '1px solid var(--border-hair)' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 160px', fontWeight: 600, fontSize: '1.02rem' }}>
                  <span aria-hidden="true" style={{ marginRight: 8 }}>{flag}</span>{label}
                </div>

                <Switch
                  checked={isOn}
                  onChange={(on) => toggleLanguage(key, on)}
                  disabled={isOnlyOne}
                  label={taf('Lingua {lingua}', { lingua: label })}
                  onText={ta('Attiva')}
                  offText={ta('Non attiva')}
                />

                <label
                  className="checkbox-row"
                  style={{ margin: 0, opacity: isOn ? 1 : 0.4, whiteSpace: 'nowrap' }}
                >
                  <input
                    type="radio"
                    name="default-language"
                    checked={defaultLang === key}
                    disabled={!isOn}
                    onChange={() => { setDefaultLang(key); setSaved(false); }}
                  />
                  <span>{ta('Predefinita')}</span>
                </label>
              </div>

              <div style={{ marginTop: 8, fontSize: '0.8rem', color: 'var(--muted-text)' }}>
                {REQUIRED_DOCS.map((d, i) => (
                  <span key={d.type}>
                    {i > 0 && ' · '}
                    {ta('PDF')} {ta(d.label)} {docs[`${d.type}:${key}`] ? '✓' : '✗'}
                  </span>
                ))}
              </div>

              {warn && (
                <div style={{ marginTop: 8, background: 'rgba(232,163,61,0.14)', color: '#7a5413', borderRadius: 'var(--radius-control)', padding: '8px 12px', fontSize: '0.82rem' }}>
                  {taf('⚠ Mancano i PDF {documenti} in {lingua}: i visitatori che scelgono questa lingua vedranno e accetteranno la versione italiana. Caricali in Impostazioni → Documenti privacy/sicurezza.', { documenti: missing.map((d) => ta(d.label)).join(` ${ta('e')} `), lingua: label })}
                </div>
              )}
            </div>
          );
        })}

        <p style={{ fontSize: '0.78rem', color: 'var(--muted-text)', margin: '14px 0 18px' }}>
          {ta('Con una sola lingua attiva il selettore con le bandiere non viene mostrato. I testi delle email si personalizzano in Email di benvenuto (e in Email contatti): per una lingua lasciata vuota viene usato il testo predefinito in quella stessa lingua.')}
        </p>

        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving}
          style={{ width: 'auto', padding: '10px 20px' }}
        >
          {saving ? ta('Salvataggio…') : ta('Salva')}
        </button>
      </div>
    </div>
  );
}
